import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Task } from '@modules/sales/models/task.model';
import {
  HsFormFieldModule,
  HsInputModule,
  HsSelectModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HsLayerModule } from '@hsetu/ngui-resources';
import { TaskTypeService } from '@modules/sales/services/task-type.service';
import {
  ICON_CLOCK,
  ICON_INVOICE,
  ICON_FILE,
} from '@shared/ui/icons/icon-constants';
import { FormsModule } from '@angular/forms';
import { OfferService } from '@modules/sales/services/offer.service';
import { TaskService } from '@modules/sales/services/task.service';
import { InvoiceService } from '@modules/sales/services/invoice.service';
import { AppointmentService } from '@modules/sales/services/appointment.service';
import { PaymentTermService } from '@modules/payment-terms/services/payment-term.service';
import { KfmCardComponent } from '@shared/ui/components/features/card/common/kfm-card.component';
import { PaymentTerm } from '@modules/payment-terms/models/payment-term.model';
import {
  APPOINTMENT_DATA_LABEL,
  AUTOMATICALLY_LABEL,
  CREATED_AT_LABEL,
  DESCRIPTION_LABEL,
  DOCUMENT_NUMBER_LABEL,
  INVOICE_DATA_LABEL,
  LAST_CHANGED_LABEL,
  NAME_LABEL,
  OFFER_DATA_LABEL,
  PAYMENT_METHOD_LABEL,
  TASK_DATA_LABEL,
  CHOOSE_SALES_PAYMENT_TERM_LABEL,
} from '@modules/sales/i18n/label-text-constants';

@Component({
  selector: 'kfm-sales-details-card',
  imports: [
    CommonModule,
    FormsModule,
    HsFormFieldModule,
    HsLayerModule,
    HsInputModule,
    KfmCardComponent,
    HsSelectModule,
  ],
  templateUrl: './kfm-sales-details-card.component.html',
})
export class KfmSalesDetailsCardComponent {
  private readonly _datePipe = new DatePipe('de-DE');
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _taskTypeService = inject(TaskTypeService);
  private readonly _offerService = inject(OfferService);
  private readonly _taskService = inject(TaskService);
  private readonly _invoiceService = inject(InvoiceService);
  private readonly _appointmentService = inject(AppointmentService);
  private readonly _paymentTermService = inject(PaymentTermService);

  protected readonly _salesItem = signal<Task | null>(null);
  private readonly _paymentTerms = signal<PaymentTerm[]>([]);

  public readonly appointmentChanged = output<void>();
  public readonly salesItemChanged = output<Task>();

  protected readonly cardTitle = computed<string>(() => {
    const salesItem = this._salesItem();

    if (!salesItem) return '';

    if (this._taskTypeService.isAppointment(salesItem)) {
      return this.appointmentDataLabel;
    } else if (this._taskTypeService.isTask(salesItem)) {
      return this.taskDataLabel;
    } else if (this._taskTypeService.isOffer(salesItem)) {
      return this.offerDataLabel;
    } else if (this._taskTypeService.isInvoice(salesItem)) {
      return this.invoiceDataLabel;
    }
  });

  protected readonly documentNumberLabel = DOCUMENT_NUMBER_LABEL;
  protected readonly nameLabel = NAME_LABEL;
  protected readonly descriptionLabel = DESCRIPTION_LABEL;
  protected readonly paymentMethodLabel = PAYMENT_METHOD_LABEL;
  protected readonly appointmentDataLabel = APPOINTMENT_DATA_LABEL;
  protected readonly taskDataLabel = TASK_DATA_LABEL;
  protected readonly offerDataLabel = OFFER_DATA_LABEL;
  protected readonly invoiceDataLabel = INVOICE_DATA_LABEL;
  protected readonly automaticallyLabel = AUTOMATICALLY_LABEL;
  protected readonly createdAtLabel = CREATED_AT_LABEL;
  protected readonly lastChangedLabel = LAST_CHANGED_LABEL;
  protected readonly chooseSalesPaymentTermLabel =
    CHOOSE_SALES_PAYMENT_TERM_LABEL;

  protected readonly cardIcon = computed<SafeHtml>(() => {
    const salesItem = this._salesItem();

    if (!salesItem) return null;

    if (this._taskTypeService.isAppointment(salesItem)) {
      return this._sanitizer.bypassSecurityTrustHtml(ICON_CLOCK);
    } else if (
      this._taskTypeService.isTask(salesItem) ||
      this._taskTypeService.isOffer(salesItem)
    ) {
      return this._sanitizer.bypassSecurityTrustHtml(ICON_FILE);
    } else if (this._taskTypeService.isInvoice(salesItem)) {
      return this._sanitizer.bypassSecurityTrustHtml(ICON_INVOICE);
    }
  });

  protected readonly isAppointment = computed(() => {
    const salesItem = this._salesItem();
    return salesItem ? this._taskTypeService.isAppointment(salesItem) : false;
  });

  protected readonly title = computed(() => {
    const salesItem = this._salesItem();
    return salesItem ? (salesItem.infoDesc ?? '') : '';
  });

  protected readonly number = computed(() => {
    const salesItem = this._salesItem();
    return salesItem
      ? (salesItem.number ?? this.automaticallyLabel)
      : this.automaticallyLabel;
  });

  protected readonly description = computed(() => {
    const salesItem = this._salesItem();

    if (!salesItem || !salesItem.longDesc) return '';

    return salesItem.longDesc.trim();
  });

  protected createdAt = computed(() => {
    const salesItem = this._salesItem();

    if (!salesItem) return '';

    return `${this.createdAtLabel} ${this._datePipe.transform(salesItem.stampCreate, 'dd.MM.yyyy HH:mm')}`;
  });

  protected lastEdited = computed(() => {
    const salesItem = this._salesItem();

    if (!salesItem) return '';

    if (salesItem.stampEdit) {
      return `${this.lastChangedLabel} ${this._datePipe.transform(salesItem.stampEdit, 'dd.MM.yyyy HH:mm')}`;
    }

    return '';
  });

  protected readonly isInvoice = computed(() => {
    const salesItem = this._salesItem();
    return salesItem ? this._taskTypeService.isInvoice(salesItem) : false;
  });

  protected readonly paymentTerms = computed<HsSelectOption<string>[]>(() => {
    const paymentTerms = this._paymentTerms();

    return paymentTerms
      ? paymentTerms.map((term): HsSelectOption<string> => ({ value: term.uId, label: term.name }))
      : [];
  });

  protected readonly selectedPaymentTerm = computed<string>(() => {
    const salesItem = this._salesItem();

    if (!salesItem || !this._taskTypeService.isInvoice(salesItem)) {
      return '';
    }

    return salesItem.paymentTerm?.uId || '';
  });

  @Input({ required: true })
  public set salesItem(value: Task) {
    if (!value) {
      this._salesItem.set(value);
      return;
    }

    if (this._taskTypeService.isInvoice(value)) {
      this._paymentTermService.getAll().then((terms) => {
        this._paymentTerms.set(terms.data);
        this._salesItem.set(value);
      });
    } else {
      this._salesItem.set(value);
    }
  }

  private _textareaRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('descTextarea');

  constructor() {
    // Dieser Effect läuft jedes Mal, wenn description() sich ändert
    effect(() => {
      const text = this.description();
      const element = this._textareaRef()?.nativeElement;

      if (element) {
        element.value = text;
      }
    });
  }

  public isEditable = input<boolean>(true);

  protected sanitizeHtml(html: string) {
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }

  protected onTitleChanged(title: string) {
    const salesItem = this._salesItem();

    if (this.isAppointment()) {
      salesItem.infoDesc = title;
      salesItem.appointment.subject = title;
    } else {
      salesItem.infoDesc = title;
    }

    this.saveSalesItem();
  }

  protected onDescriptionChanged(description: string) {
    const salesItem = this._salesItem();

    if (this.isAppointment()) {
      salesItem.longDesc = description;
      salesItem.appointment.infoDesc = description;
    } else {
      salesItem.longDesc = description;
    }

    this.saveSalesItem();
  }

  protected onPaymentTermChanged(option: HsSelectOption<string>) {
    const salesItem = this._salesItem();

    if (!this._taskTypeService.isInvoice(salesItem)) {
      return;
    }

    const selectedTerm = this._paymentTerms().find(
      (term) => term.uId === option.value,
    );

    salesItem.paymentTerm = selectedTerm || null;

    this.saveSalesItem();
  }

  private async saveSalesItem() {
    const salesItem = this._salesItem();

    if (this._taskTypeService.isAppointment(salesItem)) {
      await this._appointmentService.update(
        salesItem.appointment,
        undefined,
        salesItem.customer?.uId,
        salesItem.location?.uId,
      );
      this.appointmentChanged.emit();
      return;
    }

    if (this._taskTypeService.isTask(salesItem)) {
      await this._taskService.update(salesItem);
      this.appointmentChanged.emit();
    } else if (this._taskTypeService.isOffer(salesItem)) {
      await this._offerService.update(salesItem);
    } else if (this._taskTypeService.isInvoice(salesItem)) {
      await this._invoiceService.update(salesItem);
    }

    this.salesItemChanged.emit({ ...salesItem });
  }
}
